import React, { MutableRefObject, useCallback, useMemo } from 'react';
import { UpdateToEventView } from '@mark43/cad-api';
import { FederatedSearchParsedResult } from 'mark43-federated-search/dist/lib/types';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useFederatedSearchBootstrap } from './use-federated-search-bootstrap';
import { useUpdateToEventState } from './use-update-to-event-state';
import { UpdateToEventButton } from './update-to-event-button';
import { EventAutocomplete, StylesOverride } from './types';
import { getDisabledMessage } from './helpers';
import { isRmsDmvOrNletsResult } from '../utils';
import { BulkBootstrapResult } from './use-update-to-event-bulk-bootstrap';

export function FederatedSearchUpdateToEvent({
    queryId,
    queryResultId,
    linkedEventId,
    federatedSearchParsedResult,
    isEligibleForResultActions,
    bulkBootstrapResult,
    unitAssignedEventId,
    showEntitiesPopoverRef,
    EventAutocompleteComponent,
    stylesOverride = {},
    showLeadingVisualIcon,
    showTrailingVisualIcon,
    onUpdateToEventSuccess,
}: {
    queryId: string;
    queryResultId?: string;
    linkedEventId?: number;
    federatedSearchParsedResult?: FederatedSearchParsedResult;
    isEligibleForResultActions?: boolean;
    bulkBootstrapResult?: BulkBootstrapResult;
    unitAssignedEventId?: number | null;
    showEntitiesPopoverRef?: MutableRefObject<(() => void) | undefined>;
    EventAutocompleteComponent?: EventAutocomplete;
    stylesOverride?: StylesOverride;
    showLeadingVisualIcon?: boolean;
    showTrailingVisualIcon?: boolean;
    onUpdateToEventSuccess?: () => void;
}) {
    const enabledFeatures = useEnabledFeatures();
    const isUnassignedUnit = unitAssignedEventId === null;
    const initialEventId = linkedEventId ?? unitAssignedEventId ?? undefined;

    const {
        bootstrapEventId,
        bootstrapActive,
        setEventId,
        enableBootstrap,
        popoverState,
        popoverDispatch,
    } = useUpdateToEventState({ initialEventId });

    // When the user selects a different event in the popover, we need an individual
    // fetch even in bulk mode — the bulk data only covers the original eventId.
    const userChangedEvent = bootstrapEventId !== initialEventId;

    const {
        data: individualData,
        isLoading: individualIsLoading,
        isError: individualIsError,
        refetch,
    } = useFederatedSearchBootstrap({
        queryId,
        queryResultId,
        eventId: bootstrapEventId,
        enabled: (!bulkBootstrapResult || userChangedEvent) && bootstrapActive && !!enabledFeatures?.dexParsingEnabled,
    });

    const bulkQueryResult =
        bulkBootstrapResult && queryResultId
            ? bulkBootstrapResult.data.get(queryResultId)
            : undefined;

    const updateToEventView: UpdateToEventView | undefined = useMemo(() => {
        // Prefer individual data when available (e.g. user selected a different event)
        const first = individualData?.updateToEventQueryResults?.[0];
        if (first) {
            return {
                showUpdateToEventButton: first.showUpdateToEventButton,
                eventAutocompleteView: individualData!.eventAutocompleteView,
                eventPersons: first.eventPersons,
                eventVehicles: first.eventVehicles,
                matchingEventPerson: first.matchingEventPerson,
                matchingEventVehicle: first.matchingEventVehicle,
                parsedPerson: first.parsedPerson,
                parsedVehicle: first.parsedVehicle,
                eventNumber: individualData!.eventAutocompleteView?.agencyEventNumber,
                eventId: individualData!.eventAutocompleteView?.agencyEventId,
            };
        }
        if (bulkQueryResult) {
            return {
                showUpdateToEventButton: bulkQueryResult.showUpdateToEventButton,
                eventAutocompleteView: bulkQueryResult.eventAutocompleteView,
                eventPersons: bulkQueryResult.eventPersons,
                eventVehicles: bulkQueryResult.eventVehicles,
                matchingEventPerson: bulkQueryResult.matchingEventPerson,
                matchingEventVehicle: bulkQueryResult.matchingEventVehicle,
                parsedPerson: bulkQueryResult.parsedPerson,
                parsedVehicle: bulkQueryResult.parsedVehicle,
                eventNumber: bulkQueryResult.eventAutocompleteView?.agencyEventNumber,
                eventId: bulkQueryResult.eventAutocompleteView?.agencyEventId,
            };
        }
        return undefined;
    }, [bulkQueryResult, individualData]);

    const isRmsDmvOrNlets = isRmsDmvOrNletsResult(
        federatedSearchParsedResult,
        isEligibleForResultActions
    );
    const useIndividualStatus = !bulkBootstrapResult || userChangedEvent;
    const isBootstrapError = useIndividualStatus ? individualIsError : bulkBootstrapResult!.isError;
    const isBootstrapLoading = useIndividualStatus ? individualIsLoading : bulkBootstrapResult!.isLoading;

    const shouldShowButton =
        (!!updateToEventView?.showUpdateToEventButton &&
            (!federatedSearchParsedResult || !!isRmsDmvOrNlets)) ||
        popoverState.isPopoverOpen ||
        popoverState.isConfirmModalOpen;

    const disabledMessage = getDisabledMessage(isBootstrapError, isUnassignedUnit);

    const handleUpdateSuccess = useCallback(() => {
        if (!bulkBootstrapResult || userChangedEvent) {
            refetch();
        }
        onUpdateToEventSuccess?.();
    }, [bulkBootstrapResult, userChangedEvent, refetch, onUpdateToEventSuccess]);

    if (!shouldShowButton) {
        return null;
    }

    return (
        <UpdateToEventButton
            updateToEventView={
                (bootstrapEventId || updateToEventView?.eventId)
                    ? updateToEventView
                    : undefined
            }
            setEventId={setEventId}
            isBootstrapLoading={isBootstrapLoading}
            onUpdateToEventSuccess={handleUpdateSuccess}
            disabledMessage={disabledMessage}
            showEntitiesPopoverRef={showEntitiesPopoverRef}
            EventAutocompleteComponent={EventAutocompleteComponent}
            stylesOverride={stylesOverride}
            onShowEntitiesPopover={enableBootstrap}
            showLeadingVisualIcon={showLeadingVisualIcon}
            showTrailingVisualIcon={showTrailingVisualIcon}
            popoverState={popoverState}
            popoverDispatch={popoverDispatch}
        />
    );
}
